
import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator'
import ViewModel from '@/models/ViewModel'
import SystemDashboardAction from '@/models/SystemDashboardAction'
import DatePicker from '@/components/DatePicker/DatePicker.vue'
import DatePickerDate from '@/models/DatePickerDate'
import moment from 'moment'
import SelectPicker from '@/components/SelectPicker/SelectPicker.vue'
import Company from '@/models/Company'
import SelectOption from '@/models/interface/SelectOption'
import FilterHeader from './FilterHeader.vue'

// Label for a delivery_goals entry — uses `name` when set, falls back to a
// formatted date range. Kept local to this component because the loader for
// `goal-select` lives here.
const formatGoalLabel = (g: any): string => {
  if (g?.name) {
    return g.name
  }
  const start = g?.start_at ? moment(g.start_at).format('MMM D, YYYY') : ''
  const end = g?.end_at ? moment(g.end_at).format('MMM D, YYYY') : ''
  return start && end ? `${start} – ${end}` : 'Untitled goal'
}

@Component({
  components: {
    DatePicker,
    FilterHeader,
    SelectPicker,
  },
})
export default class DashboardAction extends ViewModel {
  @Prop({ required: true })
  public action!: SystemDashboardAction

  @Prop({ required: true })
  public value!: any

  @Prop()
  public secondaryValue!: any

  public localValue!: any

  public localSecondaryValue!: any

  public dateValue: DatePickerDate = new DatePickerDate()

  // Options loaded for action.type === 'goal-select'. Owned by THIS component
  // so the SelectPicker prop binding reactively reflects the fetch result —
  // no cross-component array-mutation reactivity gap.
  public goalOptions: SelectOption[] = []

  protected _goalsLoaded: boolean = false

  /**
   * For `toggle` actions (a single click-to-flip button between exactly two
   * options): the option that is NOT currently active — i.e. the action a click
   * performs. Its `detail` (line-awesome icon name) renders as the button icon
   * with the text as tooltip; without an icon the text itself is the label.
   * Reads `value` directly so the button updates as the filter changes.
   */
  public get toggleNextOption() {
    const options = this.action.options || []

    return options.find((option: any) => option.value !== this.value) ?? options[0]
  }

  public get isDatePicker() {
    return [
      'date',
      'date_range',
      'datetime',
      'datetime_range',
      'year',
      'year_range',
      'time',
      'time_range',
    ].includes(this.action.type)
  }

  public get dateType() {
    if (this.action.type.includes('datetime')) {
      return 'datetime'
    }
    if (this.action.type.includes('time')) {
      return 'time'
    }
    if (this.action.type.includes('year')) {
      return 'year'
    }

    return 'date'
  }

  public get dateFormat() {
    if (this.action.type.includes('datetime')) {
      return 'YYYY-MM-DD HH:mm:ss'
    }
    if (this.action.type.includes('time')) {
      return 'HH:mm:ss'
    }
    return 'YYYY-MM-DD'
  }

  @Watch('value', { immediate: true })
  public onValueChange() {
    if (this.action.secondaryKey) {
      return
    }
    this.updateLocalValue()
  }

  @Watch('secondaryValue', { immediate: true })
  public onSecondaryValueChange() {
    if (!this.action.secondaryKey) {
      return
    }
    this.updateLocalValue()
  }

  protected updateLocalValue() {
    if (this.isDatePicker) {
      this.dateValue = new DatePickerDate(this.value, this.secondaryValue)
    } else {
      this.localValue = this.value
    }
  }

  /**
   * Build text label based on value
   */
  public getFilterLabel() {
    const value = this.action.options.find((option: any) => option.value === this.value)?.name
    if (!value) {
      return this.action.label
    }
    return `${this.action.label}: ${value}`
  }

  public onClick(value: any) {
    this.$emit('action', { action: this.action, value, refresh: true })
  }

  /**
   * `month` actions: the calendar month picker speaks plain YYYY-MM strings
   * both ways. The picker re-emits on mount, so only propagate real changes.
   */
  public onMonthInput(value: any) {
    if (!value || value === this.localValue) {
      return
    }

    this.localValue = value
    this.onClick(value)
  }

  public onDateChange() {
    setTimeout(() => {
      this.$emit('action', {
        action: this.action,
        value: [
          moment(this.dateValue.start).format(this.dateFormat),
          moment(this.dateValue.end).format(this.dateFormat),
        ],
        refresh: true,
      })
    }, 100)
  }

  public onCheckboxChange(fields: any = null) {
    let value: any[] = []

    this.action.options.forEach((key: any) => {
      value.push(this.localValue.includes(key.value))
    })

    this.$emit('action', {
      action: this.action,
      value: fields || value,
      refresh: true,
    })
  }

  public mounted() {
    this.updateLocalValue()
    if (this.action.type === 'goal-select') {
      this.loadGoalOptions()
    }
  }

  protected loadGoalOptions() {
    if (this._goalsLoaded) {
      return
    }
    const companyId = (this.action as any).companyId
    if (!companyId) {
      return
    }
    this._goalsLoaded = true
    Company.fetchById(companyId)
      .then((company: any) => {
        const goals = Array.isArray(company?.delivery_goals) ? company.delivery_goals : []
        this.goalOptions = goals.map((g: any) => new SelectOption(formatGoalLabel(g), g.id))
      })
      .catch((err: any) => {
        // eslint-disable-next-line no-console
        console.error('[goal-select] failed to load company', err)
      })
  }
}
